import { contentTypeParser } from "@circle-react-shared/uikit/TipTap/utilities/contentTypeParser";
import { mediaTranscodingsApi } from "@circle-react/api/mediaTranscodingsApi";
import { useUppyUpload } from "@circle-react/components/Spaces/CourseSpace/Lessons/Edit/Content/LessonContent/useUppyUpload";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { activeStorageBlobUrl } from "@circle-react/helpers/urlHelpers";
import type { MediaTranscoding } from "@circle-react/types/MediaTranscoding";
import { useCallback, useState } from "react";
import { useMutation } from "react-query";

interface UploadedFile {
  [key: string]: any;
  content_type: string;
  filename: string;
  signed_id: string;
  url: string;
}

interface UploadedMediaFile extends UploadedFile {
  media_transcoding: MediaTranscoding;
}

type UploadStatus = "idle" | "preparing" | "uploading" | "succeeded" | "failed";

type UploadError = "upload_failed";

interface FileUploadOptions {
  onComplete: (file: UploadedFile | UploadedMediaFile) => void;
}

export const useFileUpload = ({ onComplete }: FileUploadOptions) => {
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [error, setError] = useState<UploadError | null>(null);

  const { mutate: startTranscodingMedia } = useMutation<
    MediaTranscoding,
    ApiError,
    UploadedFile
  >({
    mutationFn: file =>
      mediaTranscodingsApi.create({ blobSignedId: file.signed_id }),
  });

  const { uppy, progress } = useUppyUpload({
    onUploadStarted: () => setStatus("uploading"),
    onUploadComplete: (file: UploadedFile) => {
      const isTranscodableMedia = contentTypeParser.isVideo(file.content_type);

      const { signed_id, filename } = file;
      const url = activeStorageBlobUrl({ signed_id, filename });

      if (!isTranscodableMedia) {
        onComplete({ ...file, url });
        setStatus("succeeded");
        return;
      }

      startTranscodingMedia(file);

      onComplete({
        ...file,
        url,
        media_transcoding: { status: "pending" },
      });
      setStatus("succeeded");
    },
    onUploadFailed: () => {
      setError("upload_failed");
      setStatus("failed");
    },
  });

  const addFile = useCallback(
    (file: any) => {
      setStatus("preparing");
      setError(null);

      uppy.addFile(file);
    },
    [uppy],
  );

  const cancelUpload = useCallback(() => {
    setStatus("idle");
    setError(null);

    uppy.cancelAll();
  }, [uppy]);

  return {
    status,
    progress,
    error,
    addFile,
    cancelUpload,
  };
};
